/**
 * Locale Resolution Waterfall Utilities
 * SHOP-563: Deterministic locale resolution with priority-based evaluation
 */

window.LocaleResolver = window.LocaleResolver || {};

LocaleResolver.Utils = {
  COOKIE_NAME: 'user_locale_preference',
  COOKIE_TTL_DAYS: 395, // 13 months (GDPR compliant)

  /**
   * Priority 1: Get locale from cookie (sticky user selection)
   */
  getLocaleFromCookie() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.COOKIE_NAME) {
        return value ? decodeURIComponent(value) : null;
      }
    }
    return null;
  },

  /**
   * Set locale cookie with 13-month expiry
   */
  setLocaleCookie(locale, country) {
    const value = JSON.stringify({ locale, country });
    const expires = new Date();
    expires.setDate(expires.getDate() + this.COOKIE_TTL_DAYS);
    
    document.cookie = `${this.COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  },

  /**
   * Delete locale cookie
   */
  deleteLocaleCookie() {
    document.cookie = `${this.COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },

  /**
   * Priority 2: Extract locale from URL path
   * Examples: /en-us/products, /de-de/collections
   */
  getLocaleFromURL() {
    const pathname = window.location.pathname;
    const localePattern = /^\/([a-z]{2}-[a-z]{2})(\/|$)/i;
    const match = pathname.match(localePattern);
    
    if (match) {
      const [, localeCode] = match;
      const [language, country] = localeCode.split('-');
      return {
        locale: language.toLowerCase(),
        country: country.toUpperCase(),
        fullCode: localeCode.toLowerCase()
      };
    }
    
    return null;
  },

  /**
   * Priority 3: Get country from GeoIP (Shopify API)
   */
  async getCountryFromGeoIP() {
    try {
      // Check Cloudflare header first (if available via meta tag or server-side)
      const cfCountry = this.getCloudflareCountry();
      if (cfCountry) {
        return cfCountry;
      }

      // Fallback to Shopify's geolocation API
      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();
      return data.detected_values.country;
    } catch (error) {
      console.error('GeoIP lookup failed:', error);
      return null;
    }
  },

  /**
   * Check for Cloudflare CF-IPCountry header (if exposed via meta tag)
   */
  getCloudflareCountry() {
    const metaTag = document.querySelector('meta[name="cf-ipcountry"]');
    if (metaTag && metaTag.content) {
      return {
        handle: metaTag.content.toUpperCase(),
        name: metaTag.content.toUpperCase()
      };
    }
    return null;
  },

  /**
   * Priority 4: Get default locale from theme settings
   */
  getDefaultLocale() {
    // This will be populated from Liquid template
    return window.LocaleResolver.defaultLocale || {
      locale: 'en',
      country: 'IE'
    };
  },

  /**
   * Validate if country is in available countries list
   */
  isCountryAvailable(countryCode) {
    if (!window.Woolman?.geoInfo?.availableCountries) return false;
    
    return window.Woolman.geoInfo.availableCountries.some(
      country => country.isocode === countryCode
    );
  },

  /**
   * Get locale for a specific country (mapping logic)
   * Preserves current locale if it's a common language, otherwise uses country default
   */
  getLocaleForCountry(countryCode, currentLocale = null) {
    // Try to preserve current locale if it's a common international language
    const commonLanguages = ['en', 'de', 'fr', 'es'];
    
    if (currentLocale && commonLanguages.includes(currentLocale)) {
      // Keep current language if it's a common one
      // This allows en-se, en-ie, etc.
      return currentLocale;
    }

    // Default country-locale mapping for country-specific languages
    const countryLocaleMap = {
      'SE': 'sv',
      'DE': 'de',
      'FR': 'fr',
      'ES': 'es',
      'IT': 'it',
      'FI': 'fi'
    };
    
    return countryLocaleMap[countryCode] || 'en';
  },

  /**
   * Main Waterfall Resolution Logic
   * Returns: { locale, country, source }
   */
  async resolveLocale() {
    // CRITICAL: Check URL FIRST to detect explicit user navigation
    // If user manually visits /en-se while having sv-SE cookie,
    // we should respect the URL (explicit choice)
    const urlLocale = this.getLocaleFromURL();
    const currentUrlCountry = urlLocale?.country;
    const currentUrlLocale = urlLocale?.locale;

    // Priority 1: Cookie (Explicit User Selection)
    const cookieData = this.getLocaleFromCookie();
    
    if (cookieData) {
      try {
        const parsed = JSON.parse(cookieData);
        
        if (parsed.locale && parsed.country && this.isCountryAvailable(parsed.country)) {
          // SHOP-563 BUG FIX: If URL has different locale for SAME country,
          // respect URL (user explicitly navigating)
          if (currentUrlCountry === parsed.country && currentUrlLocale && currentUrlLocale !== parsed.locale) {
            // User explicitly visiting different locale for same country
            // Example: Cookie has sv-SE, but user visits /en-se
            // Respect URL and update cookie
            this.setLocaleCookie(currentUrlLocale, currentUrlCountry);
            return {
              locale: currentUrlLocale,
              country: currentUrlCountry,
              source: 'url' // URL took priority over cookie
            };
          }
          
          // Normal cookie flow - no conflict
          return {
            locale: parsed.locale,
            country: parsed.country,
            source: 'cookie'
          };
        }
      } catch (e) {
        this.deleteLocaleCookie();
      }
    }

    // Priority 2: URL Path
    if (urlLocale && this.isCountryAvailable(urlLocale.country)) {
      return {
        locale: urlLocale.locale,
        country: urlLocale.country,
        source: 'url'
      };
    }

    // Priority 3: GeoIP Lookup
    const geoCountry = await this.getCountryFromGeoIP();
    
    if (geoCountry?.handle && this.isCountryAvailable(geoCountry.handle)) {
      const resolvedLocale = this.getLocaleForCountry(geoCountry.handle);
      return {
        locale: resolvedLocale,
        country: geoCountry.handle,
        source: 'geoip'
      };
    }

    // Priority 4: Default Locale
    const defaultLocale = this.getDefaultLocale();
    return {
      locale: defaultLocale.locale,
      country: defaultLocale.country,
      source: 'default'
    };
  },

  /**
   * Check if redirect is needed
   */
  shouldRedirect(resolved) {
    const currentCountry = window.Shopify?.country;
    const currentLocale = window.Shopify?.locale;
    
    return resolved.country !== currentCountry || resolved.locale !== currentLocale;
  },

  /**
   * Build redirect URL with proper market subfolder
   */
  buildRedirectURL(locale, country) {
    const domain = window.location.hostname;
    const currentUrl = new URL(window.location.href);
    
    let marketSubfolder = '';
    
    // Special cases
    if (country === 'DE') {
      marketSubfolder = 'de-de';
    } else if (country === 'SE') {
      marketSubfolder = 'sv-se';
    } else if (country === 'IE') {
      marketSubfolder = '';
    } else {
      marketSubfolder = `${locale}-${country.toLowerCase()}`;
    }

    // Remove existing locale from pathname
    const rootPath = window.Shopify?.routes?.root?.replace(/\/$/, '') || '';
    let newPathname = window.location.pathname.replace(new RegExp(`^${rootPath}(\\/|$)`), '/');
    
    // Remove any existing locale pattern
    newPathname = newPathname.replace(/^\/[a-z]{2}-[a-z]{2}(\/|$)/, '/');
    
    // Remove double slashes
    newPathname = newPathname.replace(/\/+/g, '/');

    // Build new URL
    const queryString = currentUrl.searchParams.toString();
    const finalUrl = `https://${domain}${marketSubfolder ? '/' + marketSubfolder : ''}${newPathname}${queryString ? '?' + queryString : ''}`;
    
    return finalUrl;
  },

  /**
   * Migrate old localStorage data to cookie.
   * Keys are read from Woolman.geoInfo to stay in sync with the original storage contract.
   */
  migrateFromLocalStorage() {
    const countryKey = window.Woolman?.geoInfo?.userCountryKey || 'userCountry';
    const localeKey  = window.Woolman?.geoInfo?.userCountryLocale || 'userLocale';

    const oldCountry = localStorage.getItem(countryKey);
    const oldLocale  = localStorage.getItem(localeKey);

    if (
      oldCountry && oldLocale &&
      oldCountry.toLowerCase() !== 'null' &&
      oldLocale.toLowerCase()  !== 'null'
    ) {
      this.setLocaleCookie(oldLocale, oldCountry);

      // Clean up old keys
      localStorage.removeItem(countryKey);
      localStorage.removeItem(localeKey);

      console.info('[Locale Waterfall] Migrated locale preference from localStorage to cookie');
    }
  }
};
